<script setup lang="ts">
import { computed } from 'vue';

import PresenterValueFallback from '@satellite/components/elements/presenters/PresenterValueFallback.vue';

import type { PresenterDisplaySize } from '@satellite/composables/presenterDisplayOptions';
import type {
  CarrierDisplayField,
  CarrierDisplayData
} from '@satellite/composables/useCarrierDisplay';

const {
  data,
  size,
  displayFields = ['name'],
  columnGapClass = 'gap-0-5'
} = defineProps<{
  data: CarrierDisplayData;
  size?: PresenterDisplaySize;
  displayFields?: CarrierDisplayField[];
  columnGapClass?: 'gap-0-5' | 'gap-1';
}>();

const resolvedSize = computed(() => size ?? data.size);

const showCarrierName = computed(() => displayFields.includes('name'));
const showEmail = computed(() => displayFields.includes('email'));
const showPhone = computed(() => displayFields.includes('phone'));
const showCompanyName = computed(() => displayFields.includes('companyName'));
const showCompanyScac = computed(() => displayFields.includes('companyScac'));
</script>

<template>
  <div
    class="d-inline-flex flex-column justify-start carrier-display-v2"
    v-bind="$attrs"
    :class="{
      [columnGapClass]: true,
      'typography-body-sm': resolvedSize === 'small',
      'typography-body-md': resolvedSize === 'regular',
      'typography-heading-lg-bold': resolvedSize === 'large'
    }">
    <template v-if="showCarrierName">
      <slot
        name="carrier-name"
        :carrier-name="data.carrierName"
        :carrier-name-attrs="data.carrierNameAttrs">
        <presenter-value-fallback v-if="!data.carrierName" />
        <span v-else v-bind="data.carrierNameAttrs">{{ data.carrierName }}</span>
      </slot>
    </template>

    <template v-if="showEmail">
      <slot
        name="carrier-email"
        :carrier-email="data.carrierEmail"
        :carrier-email-attrs="data.carrierEmailAttrs">
        <presenter-value-fallback v-if="!data.carrierEmail" />
        <span v-else v-bind="data.carrierEmailAttrs">{{ data.carrierEmail }}</span>
      </slot>
    </template>

    <template v-if="showPhone">
      <slot
        name="carrier-phone"
        :carrier-phone="data.carrierPhone"
        :carrier-phone-attrs="data.carrierPhoneAttrs">
        <presenter-value-fallback v-if="!data.carrierPhone" />
        <span v-else v-bind="data.carrierPhoneAttrs">{{ data.carrierPhone }}</span>
      </slot>
    </template>

    <template v-if="showCompanyName">
      <slot
        name="company-name"
        :company-name="data.companyName"
        :company-name-attrs="data.companyNameAttrs">
        <presenter-value-fallback v-if="!data.companyName" :text="data.unknownCompanyMessage" />
        <span v-else v-bind="data.companyNameAttrs">
          {{ data.companyName }}
        </span>
      </slot>
    </template>

    <template v-if="showCompanyScac">
      <slot
        name="company-scac"
        :company-scac="data.companyScac"
        :company-scac-attrs="data.companyScacAttrs">
        <presenter-value-fallback v-if="!data.companyScac" />
        <span v-else v-bind="data.companyScacAttrs">
          {{ `SCAC: ${data.companyScac}` }}
        </span>
      </slot>
    </template>
  </div>
</template>
